import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getThread } from '../lib/api.js';
import { threadLabel } from '../lib/format.js';
import { LIVE_POLL_MS } from '../lib/poll.js';
import { ChannelBadge } from './ChannelBadge.js';
import { MessageRow } from './MessageRow.js';
import { ReviewWorkspace } from './ReviewWorkspace.js';
import { StatusBadge } from './StatusBadge.js';
import { EmptyState } from './states/EmptyState.js';
import { ErrorState } from './states/ErrorState.js';
import { LoadingState } from './states/LoadingState.js';

interface Props {
  threadId: string | null;
  /**
   * Forwarded to ReviewWorkspace. Called after defer / no-response
   * succeeds; the parent app decides where to send the operator next
   * (typically the next thread in the actionable queue).
   */
  onTriaged?: (currentThreadId: string, kind: 'defer' | 'mark_no_response_needed' | 'sent') => void;
}

export function ThreadDetail({ threadId, onTriaged }: Props) {
  const latestMessageRef = useRef<HTMLDivElement | null>(null);
  /**
   * Track which thread we've already auto-scrolled for. Scrolling fires
   * once per thread (when its data first lands), NOT on every refetch.
   *
   * Previous behaviour scrolled on every `query.data` change, so the
   * `IntelligencePanel` polling loop (which invalidates `['thread', id]`
   * every 2s for up to 60s) yanked the operator back to the latest
   * message ~30 times per Analyze click — making it impossible to read
   * older history while waiting for analysis.
   */
  const lastScrolledThreadId = useRef<string | null>(null);
  const query = useQuery({
    // `enabled: threadId !== null` gates execution; the `?? ''` is dead
    // code at runtime but lets us drop the non-null assertion.
    queryKey: ['thread', threadId],
    queryFn: ({ signal }) => getThread(threadId ?? '', { signal }),
    enabled: threadId !== null,
    // batch-105 — live conversation: new messages appear without a reload. The
    // ReviewWorkspace composer guards in-progress drafts against the refetch.
    // batch-166 — tightened to LIVE_POLL_MS (8s).
    refetchInterval: LIVE_POLL_MS,
  });

  useEffect(() => {
    if (!query.data) return;
    if (threadId === null) return;
    if (lastScrolledThreadId.current === threadId) return;
    if (typeof latestMessageRef.current?.scrollIntoView !== 'function') return;
    latestMessageRef.current.scrollIntoView({ block: 'end' });
    lastScrolledThreadId.current = threadId;
  }, [query.data, threadId]);

  if (threadId === null) {
    return (
      <EmptyState
        title="Select a thread"
        hint="Pick a conversation from the queue to read its history."
      />
    );
  }

  if (query.isPending) return <LoadingState label="Loading thread" />;
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const thread = query.data;
  const contact = threadLabel(thread.participants, thread.subject, thread.channel);

  return (
    // flex-1 (not h-full): a sibling .unified-canvas-triage bar sits above us in the
    // flex-column .unified-conversation, so we must fill the REMAINING height, not 100%
    // — otherwise the composer's bottom (Send/Save) overflows the pane and is clipped.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-zinc-800/80 px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold tracking-tight text-zinc-100">
            {contact.label}
          </div>
          {thread.subject ? (
            <div className="mt-0.5 truncate text-[12px] text-zinc-500">{thread.subject}</div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <ChannelBadge channel={thread.channel} />
          <StatusBadge status={thread.review_status} />
        </div>
      </div>

      <div
        data-testid="message-thread"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4"
      >
        {thread.messages.length === 0 ? (
          <EmptyState title="No messages in this thread" />
        ) : (
          <>
            {thread.messages.map((m) => (
              <MessageRow key={m.id} message={m} showSender={thread.is_group} />
            ))}
            <div ref={latestMessageRef} data-testid="latest-message-anchor" />
          </>
        )}
      </div>

      <ReviewWorkspace thread={thread} onTriaged={onTriaged} />
    </div>
  );
}
